import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  LinearProgress,
  Divider,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Button,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  CloudQueue as CloudQueueIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  HelpOutline as HelpOutlineIcon,
  Storage as StorageIcon,
  SmartToy as AgentIcon,
  Api as ApiIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { formatDistanceToNow } from 'date-fns';
import { api, Service, ServicesResponse, AuditLog } from '../services/api-client';
import { useAuth } from '../contexts/GoogleAuthContext';
import { useNavigate } from 'react-router-dom';

// =============================================================================
// Types
// =============================================================================

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
  loading?: boolean;
  onClick?: () => void;
}

interface AgentData {
  id: string;
  name?: string;
  allowed_services?: string[];
  services?: string[];
}

interface PermissionsResponse {
  agents: AgentData[];
}

// =============================================================================
// Components
// =============================================================================

function StatCard({ title, value, icon, color, subtitle, loading, onClick }: StatCardProps) {
  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        '&:hover': onClick ? {
          boxShadow: 3,
          transform: 'translateY(-2px)',
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '100%' }}>
          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {title}
            </Typography>
            {loading ? (
              <Skeleton width={60} height={40} />
            ) : (
              <Typography variant="h4" component="div">
                {value}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', minHeight: '20px' }}>
              {subtitle || '\u00A0'}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
}

function ServiceHealthItem({ service }: { service: Service }) {
  const getHealthIcon = () => {
    if (!service.active) {
      return <ErrorIcon sx={{ color: 'text.disabled', fontSize: 18 }} />;
    }
    const status = service.runtime?.health_status;
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />;
      case 'unhealthy':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 18 }} />;
      case 'unreachable':
        return <WarningIcon sx={{ color: 'warning.main', fontSize: 18 }} />;
      default:
        return <HelpOutlineIcon sx={{ color: 'text.secondary', fontSize: 18 }} />;
    }
  };

  const getTypeIcon = () => {
    switch (service.type) {
      case 'mcp_server':
        return <StorageIcon sx={{ fontSize: 14, color: 'text.secondary' }} />;
      case 'agent':
        return <AgentIcon sx={{ fontSize: 14, color: 'text.secondary' }} />;
      case 'api':
        return <ApiIcon sx={{ fontSize: 14, color: 'text.secondary' }} />;
      default:
        return <CloudQueueIcon sx={{ fontSize: 14, color: 'text.secondary' }} />;
    }
  };

  const getChipColor = (): 'success' | 'warning' | 'error' | 'default' => {
    if (!service.active) return 'default';
    const status = service.runtime?.health_status;
    switch (status) {
      case 'healthy':
        return 'success';
      case 'unhealthy':
        return 'error';
      case 'unreachable':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getProgressColor = (): 'success' | 'warning' | 'error' | 'inherit' => {
    if (!service.active) return 'inherit';
    const status = service.runtime?.health_status;
    switch (status) {
      case 'healthy':
        return 'success';
      case 'unhealthy':
        return 'error';
      case 'unreachable':
        return 'warning';
      default:
        return 'inherit';
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getHealthIcon()}
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              color: service.active ? 'text.primary' : 'text.disabled',
            }}
          >
            {service.id}
          </Typography>
          {getTypeIcon()}
        </Box>
        <Chip
          size="small"
          label={service.active ? (service.runtime?.health_status || 'unknown') : 'inactive'}
          color={getChipColor()}
          variant={service.active ? 'filled' : 'outlined'}
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      </Box>
      <LinearProgress
        variant="determinate"
        value={service.active && service.runtime?.health_status === 'healthy' ? 100 : 50}
        color={getProgressColor()}
        sx={{ height: 4, borderRadius: 2, bgcolor: 'action.hover' }}
      />
    </Box>
  );
}

function ActivityItem({ log }: { log: AuditLog }) {
  const getActionDescription = () => {
    const action = log.action || '';
    const parts = action.split('.');
    if (parts.length === 2) {
      return `${parts[1].replace(/_/g, ' ')} ${parts[0]}`;
    }
    return action.replace(/[._]/g, ' ');
  };

  const formatTimestamp = () => {
    if (!log.timestamp) return 'Unknown time';
    try {
      // Handle Firestore timestamp or ISO string
      const date = typeof log.timestamp === 'string'
        ? new Date(log.timestamp)
        : (log.timestamp as { toDate?: () => Date }).toDate?.() || new Date(log.timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return 'Unknown time';
    }
  };

  return (
    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
      <ListItemAvatar>
        <Avatar sx={{ width: 32, height: 32, fontSize: '0.9rem', bgcolor: 'primary.main' }}>
          {log.user?.email?.[0]?.toUpperCase() || '?'}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="body2">
            <strong>{log.user?.email?.split('@')[0] || 'Unknown'}</strong>
            {' '}
            {getActionDescription()}
            {log.resource?.id && (
              <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', ml: 0.5 }}>
                {log.resource.id}
              </Typography>
            )}
          </Typography>
        }
        secondary={
          <Typography variant="caption" color="text.secondary">
            {formatTimestamp()}
          </Typography>
        }
      />
    </ListItem>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManagePermissions = user?.role === 'admin' || user?.role === 'developer';

  // Fetch data
  const {
    data: servicesData,
    isLoading: servicesLoading,
    error: servicesError,
  } = useQuery<ServicesResponse>('services', () => api.getServices({ includeUsedBy: true }), {
    refetchOnWindowFocus: false,
  });

  const {
    data: permissionsData,
    isLoading: permissionsLoading,
  } = useQuery<PermissionsResponse>('agentPermissions', () => api.getAgentPermissions(), {
    refetchOnWindowFocus: false,
  });

  const {
    data: recentLogs,
    isLoading: logsLoading,
  } = useQuery<AuditLog[]>('recentAuditLogs', () => api.getAuditLogs(10), {
    refetchOnWindowFocus: false,
  });

  // Sync mutation
  const syncMutation = useMutation(() => api.syncServicesMetadata(), {
    onSuccess: () => {
      queryClient.invalidateQueries('services');
    },
  });

  // Data
  const services = servicesData?.services || [];
  const agents = permissionsData?.agents || [];

  // Calculate stats
  const totalServices = services.length;
  const healthyServices = services.filter(s => s.active && s.runtime?.health_status === 'healthy').length;
  const mcpServers = services.filter(s => s.type === 'mcp_server').length;
  const agentServices = services.filter(s => s.type === 'agent').length;
  const totalAgents = agents.length;
  const totalPermissions = agents.reduce((sum, agent) => {
    const allowed = agent.allowed_services || agent.services || [];
    return sum + allowed.length;
  }, 0);

  // Get recent activity
  const recentActivity = recentLogs?.slice(0, 5) || [];

  // Top services by health
  const topServices = services.slice(0, 6);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <DashboardIcon color="primary" sx={{ fontSize: 40 }} />
        <Box>
          <Typography variant="h4">Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back, {user?.name || user?.email?.split('@')[0]}
          </Typography>
        </Box>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Services"
            value={totalServices}
            icon={<CloudQueueIcon />}
            color="#1E88E5"
            subtitle={`${healthyServices} healthy • ${mcpServers} MCP • ${agentServices} agents`}
            loading={servicesLoading}
            onClick={() => navigate('/services')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Agents"
            value={totalAgents}
            icon={<PeopleIcon />}
            color="#00ACC1"
            subtitle="Configured in system"
            loading={permissionsLoading}
            onClick={() => navigate('/permissions')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Permissions"
            value={totalPermissions}
            icon={<SecurityIcon />}
            color="#43A047"
            subtitle="Agent→Service mappings"
            loading={permissionsLoading}
            onClick={() => navigate('/permissions')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Recent Changes"
            value={recentLogs?.length || 0}
            icon={<HistoryIcon />}
            color="#FB8C00"
            subtitle="Last 24 hours"
            loading={logsLoading}
            onClick={() => navigate('/audit-log')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Service Health */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Service Health
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {servicesData?.last_synced && (
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Synced {formatDistanceToNow(new Date(servicesData.last_synced), { addSuffix: true })}
                  </Typography>
                )}
                {canManagePermissions && (
                  <Tooltip title="Sync from Cloud Run">
                    <IconButton
                      size="small"
                      onClick={() => syncMutation.mutate()}
                      disabled={syncMutation.isLoading}
                    >
                      <RefreshIcon sx={{
                        animation: syncMutation.isLoading ? 'spin 1s linear infinite' : 'none',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {servicesLoading ? (
              <Box>
                {[1, 2, 3, 4].map(i => (
                  <Box key={i} sx={{ mb: 2 }}>
                    <Skeleton width="60%" height={20} />
                    <Skeleton height={8} sx={{ mt: 1 }} />
                  </Box>
                ))}
              </Box>
            ) : servicesError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load services
              </Alert>
            ) : topServices.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CloudQueueIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No services found
                </Typography>
                <Button
                  size="small"
                  onClick={() => syncMutation.mutate()}
                  sx={{ mt: 1 }}
                >
                  Sync from Cloud Run
                </Button>
              </Box>
            ) : (
              <Box>
                {topServices.map(service => (
                  <ServiceHealthItem key={service.id} service={service} />
                ))}
                {services.length > 6 && (
                  <Button
                    size="small"
                    onClick={() => navigate('/services')}
                    sx={{ mt: 1 }}
                  >
                    View all {services.length} services
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>

            {logsLoading ? (
              <List>
                {[1, 2, 3, 4, 5].map(i => (
                  <ListItem key={i} sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Skeleton variant="circular" width={32} height={32} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Skeleton width="80%" />}
                      secondary={<Skeleton width="40%" />}
                    />
                  </ListItem>
                ))}
              </List>
            ) : recentActivity.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No recent activity
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {recentActivity.map((log, index) => (
                  <React.Fragment key={log.id}>
                    <ActivityItem log={log} />
                    {index < recentActivity.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Agent Permissions Overview */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Agent Permissions Overview
              </Typography>
              <Button
                size="small"
                onClick={() => navigate('/permissions')}
              >
                Manage Permissions
              </Button>
            </Box>

            {permissionsLoading ? (
              <Grid container spacing={2}>
                {[1, 2, 3].map(i => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Card variant="outlined">
                      <CardContent>
                        <Skeleton width="60%" height={24} />
                        <Skeleton width="40%" height={20} sx={{ mt: 1 }} />
                        <Box sx={{ mt: 1, display: 'flex', gap: 0.5 }}>
                          <Skeleton width={60} height={24} />
                          <Skeleton width={60} height={24} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : agents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PeopleIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No agents configured
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {agents.map(agent => {
                  const allowedServices = agent.allowed_services || agent.services || [];
                  return (
                    <Grid item xs={12} sm={6} md={4} key={agent.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: 2,
                            borderColor: 'primary.main',
                          },
                        }}
                        onClick={() => navigate('/permissions')}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                            >
                              {agent.id}
                            </Typography>
                            <Chip
                              size="small"
                              label={`${allowedServices.length} service${allowedServices.length !== 1 ? 's' : ''}`}
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </Box>
                          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {allowedServices.slice(0, 3).map(service => (
                              <Chip
                                key={service}
                                label={service}
                                size="small"
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            ))}
                            {allowedServices.length > 3 && (
                              <Chip
                                label={`+${allowedServices.length - 3} more`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.65rem', height: 20 }}
                              />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
